<script>
import debounce from "debounce";
import { mapGetters } from "vuex";
import Spinner from "@/components/ui/Spinner";
import pagination from "@/mixins/pagination";
import labService from "@/services/lab";
import ModalLab from "./ModalLab.vue";

const DEBOUNCE_MS_TIMEOUT = 500;

export default {
  name: "LabList",

  mixins: [pagination],

  components: {
    Spinner,
    ModalLab,
  },

  data() {
    return {
      isLoading: false,
      searchInput: "",
      statusFilter: "active",
      labs: [],
      isModalVisible: false,
      selectedLab: {},
    };
  },

  computed: {
    ...mapGetters(["isAdmin"]),

    canManageLabs() {
      return this.isAdmin;
    },
  },

  created() {
    this.getLabs();

    this.$bus.$on("lab-created", this.onLabCreated);
    this.$bus.$on("lab-updated", this.onLabUpdated);
  },

  beforeDestroy() {
    this.$bus.$off("lab-created", this.onLabCreated);
    this.$bus.$off("lab-updated", this.onLabUpdated);
  },

  methods: {
    isActiveFilterValue() {
      if (this.statusFilter === "active") return true;
      if (this.statusFilter === "inactive") return false;
      return undefined;
    },

    async getLabs(query = {}) {
      this.isLoading = true;

      const defaultQuery = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
        search: this.searchInput,
        isActive: this.isActiveFilterValue(),
        ...query,
      };

      try {
        const res = await labService.getLabsPaginated(defaultQuery);
        this.labs = res.docs;
        this.pagination.total = res.total;
      } finally {
        this.isLoading = false;
      }
    },

    resetToFirstPage(extra = {}) {
      this.pagination.currentPage = 1;
      this.pagination.offset = 0;
      this.getLabs({ offset: 0, ...extra });
    },

    debounceInput: debounce(function debounceInput() {
      this.resetToFirstPage();
    }, DEBOUNCE_MS_TIMEOUT),

    onFilterChange() {
      this.resetToFirstPage();
    },

    onPageChange() {
      this.getLabs();
    },

    onLabCreated(lab) {
      this.labs.unshift(lab);
      this.pagination.total += 1;
    },

    onLabUpdated(updatedLab) {
      const index = this.labs.findIndex((l) => l._id === updatedLab._id);
      if (index !== -1) {
        this.$set(this.labs, index, updatedLab);
      }
    },

    showLabModal(lab) {
      this.selectedLab = lab || {};
      this.isModalVisible = true;
    },

    closeLabModal() {
      this.isModalVisible = false;
      this.selectedLab = {};
    },

    toggleActive(lab) {
      const nextIsActive = !lab.isActive;
      const actionLabel = nextIsActive ? "reactivar" : "dar de baja a";

      this.$confirm(
        `¿Desea ${actionLabel} el laboratorio "${lab.description}"?`,
        "Confirmar",
        {
          confirmButtonText: "Aceptar",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      )
        .then(() => labService.updateLab(lab._id, { isActive: nextIsActive }))
        .then((updated) => this.onLabUpdated(updated))
        .catch(() => {});
    },
  },
};
</script>

<template lang="pug">
  section.lab-list
    header.headline
      .headline__actions
        el-input.search__input(
          prefix-icon="el-icon-search"
          @input="debounceInput"
          v-model="searchInput"
          placeholder="Buscar por código o descripción"
          :clearable="true"
        )
        el-select(v-model="statusFilter" @change="onFilterChange")
          el-option(label="Activos" value="active")
          el-option(label="Inactivos" value="inactive")
          el-option(label="Todos" value="all")
        .new_lab.button.button--blue.box__icon(
          v-if="canManageLabs"
          @click='showLabModal()'
        )
          micon(name="add_circle_outline").button__icon
          span.button__text Nuevo Laboratorio
    .box
      .box__content--stretch
        table
          thead
            tr
              th Código
              th Descripción
              th Estado
              th Acciones
          tbody
            spinner(v-if="isLoading")
            tr(v-else v-for="lab in labs" :key="lab._id" @click='showLabModal(lab)')
              td {{ lab.code }}
              td {{ lab.description }}
              td
                el-tag(:type="lab.isActive ? 'success' : 'danger'" size="small")
                  | {{ lab.isActive ? 'Activo' : 'Inactivo' }}
              td.actions
                .actions-container(v-if="canManageLabs")
                  el-dropdown(trigger="click")
                    .button.button--action.el-dropdown-link(@click.stop)
                      micon(name="more_horiz")
                    el-dropdown-menu(slot="dropdown")
                      el-dropdown-item
                        div(@click="showLabModal(lab)")
                          micon(name="edit")
                          | Editar laboratorio
                      el-dropdown-item
                        div(@click.stop="toggleActive(lab)")
                          micon(:name="lab.isActive ? 'block' : 'check_circle'")
                          | {{ lab.isActive ? 'Dar de baja' : 'Reactivar' }}

      modal-lab(
        v-if='isModalVisible'
        :current-lab='selectedLab'
        :show-dialog-modal='isModalVisible'
        @close='closeLabModal'
      )

      pagination(
        :isLoading="isLoading"
        :limit="pagination.limit"
        :total="pagination.total"
        @pagination="setPagination"
      )

</template>

<style lang="scss" scoped>
@import "@/styles/settings/index.scss";
@import "@/styles/tools/index.scss";

tbody td:hover {
  cursor: pointer;
}

.box {
  height: 100%;
}

.lab-list {
  height: 65vh;
  .headline {
    .headline__actions {
      @include media(mobile-up) {
        width: 100%;
      }
      @include media(web-up) {
        width: auto;
      }
    }
  }
}
</style>
